import React from 'react';
import { Control, Controller, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InformationCircleIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import ChannelIcon from '../ChannelIcon';

interface Props {
  control: Control<any>;
  watch: any;
  setValue: any;
}

const CONTENT_CHANNELS = [
  { value: 'instagram', labelKey: 'campaigns:registration.v2.channels.instagram' },
  { value: 'blog', labelKey: 'campaigns:registration.v2.channels.blog' },
  { value: 'youtube', labelKey: 'campaigns:registration.v2.channels.youtube' },
] as const;

const INSTAGRAM_ITEMS = ['reels', 'story', 'post_feed'] as const;
const BLOG_ITEMS = ['post'] as const;
const YOUTUBE_ITEMS = ['video', 'shorts'] as const;

const SECONDARY_TYPES = ['video', 'photo'] as const;

const CampaignMissionV2: React.FC<Props> = ({ control, watch, setValue }) => {
  const { t } = useTranslation(['campaigns', 'common']);
  const selectedChannels: string[] = watch('contentChannels') || [];
  const rewardAmount = watch('rewardAmount') || 0;
  const influencerAmount = Math.round(rewardAmount * 0.8);
  const alwaysOpening = watch('alwaysOpening') || false;
  const discussSchedule = watch('discussSchedule') || false;

  // Publishing conditions field arrays (V2 — nested under publishingConditions)
  const { fields: igFields, append: igAppend, remove: igRemove } = useFieldArray({ control, name: 'publishingConditions.instagram.conditions' });
  const { fields: blogFields, append: blogAppend, remove: blogRemove } = useFieldArray({ control, name: 'publishingConditions.blog.conditions' });
  const { fields: ytFields, append: ytAppend, remove: ytRemove } = useFieldArray({ control, name: 'publishingConditions.youtube.conditions' });
  const { fields: secondaryFields, append: secondaryAppend, remove: secondaryRemove } = useFieldArray({ control, name: 'secondarySubmissions' });

  return (
    <div className="space-y-5">
      {/* 모집인원 */}
      <Controller name="numberOfRecruits" control={control}
        render={({ field }) => (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('campaigns:registration.v2.numberOfRecruits')}</label>
            <div className="flex items-center gap-2">
              <input {...field} type="number" min={0}
                onChange={e => field.onChange(parseInt(e.target.value) || 0)}
                className="w-32 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" />
              <span className="text-sm text-gray-500">{t('campaigns:registration.v2.personsUnit')}</span>
            </div>
          </div>
        )}
      />

      {/* 콘텐츠 발행 채널 (multi-select) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('campaigns:registration.v2.contentChannel')} <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-4">
          {CONTENT_CHANNELS.map(ch => {
            const isChecked = selectedChannels.includes(ch.value);
            return (
              <label key={ch.value} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={isChecked}
                  onChange={() => {
                    const current = selectedChannels;
                    const next = isChecked
                      ? current.filter((v: string) => v !== ch.value)
                      : [...current, ch.value];
                    setValue('contentChannels', next);
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <ChannelIcon channel={ch.value} />
                <span className="text-sm text-gray-700">{t(ch.labelKey)}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* 리워드 */}
      <div>
        <Controller name="rewardAmount" control={control}
          render={({ field }) => (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('campaigns:registration.v2.reward')}</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-gray-500 pointer-events-none">$</span>
                <input {...field} type="number" min={0}
                  onChange={e => field.onChange(parseFloat(e.target.value) || 0)}
                  className="w-full pl-7 pr-12 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" />
                <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-gray-500 pointer-events-none">USD</span>
              </div>
            </div>
          )}
        />
        <div className="mt-2 flex items-center gap-1">
          <span className="text-sm text-gray-600">{t('campaigns:registration.v2.influencerSettlement')}:</span>
          <span className="text-sm font-semibold text-blue-600">${influencerAmount.toLocaleString()}</span>
          <div className="relative group ml-1">
            <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-help" />
            <div className="absolute bottom-6 left-0 w-72 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
              {t('campaigns:registration.v2.settlementTooltip')}
            </div>
          </div>
        </div>
      </div>

      {/* 인스타그램 발행조건 */}
      {selectedChannels.includes('instagram') && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('campaigns:registration.v2.igConditions')}</label>
          <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-50"><tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600">#</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">{t('campaigns:registration.v2.item')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">{t('campaigns:registration.v2.quantity')}</th>
              <th className="px-3 py-2"></th>
            </tr></thead>
            <tbody>
              {igFields.map((f, i) => (
                <tr key={f.id} className="border-t border-gray-100">
                  <td className="px-3 py-2">{i + 1}</td>
                  <td className="px-3 py-2">
                    <Controller name={`publishingConditions.instagram.conditions.${i}.item`} control={control}
                      render={({ field }) => (
                        <select {...field} className="px-2 py-1 text-sm border border-gray-300 rounded">
                          <option value="">{t('common:select')}</option>
                          {INSTAGRAM_ITEMS.map(it => <option key={it} value={it}>{t(`campaigns:registration.v2.igItems.${it}`)}</option>)}
                        </select>
                      )} />
                  </td>
                  <td className="px-3 py-2">
                    <Controller name={`publishingConditions.instagram.conditions.${i}.quantity`} control={control}
                      render={({ field }) => <input {...field} type="number" min={1} className="w-16 px-2 py-1 text-sm border border-gray-300 rounded" />} />
                  </td>
                  <td className="px-3 py-2"><button type="button" onClick={() => igRemove(i)}><XMarkIcon className="w-4 h-4 text-gray-400" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" onClick={() => igAppend({ item: '', quantity: 1 })}
            className="mt-2 flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
            <PlusIcon className="w-4 h-4" /> {t('campaigns:registration.v2.addRow')}
          </button>
          {/* 공동작업자 ID */}
          <Controller name="publishingConditions.instagram.collaboratorId" control={control}
            render={({ field }) => (
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">공동작업자 ID</label>
                <input {...field} type="text" placeholder="@collaborator_id"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" />
              </div>
            )}
          />
        </div>
      )}

      {/* 블로그 발행조건 */}
      {selectedChannels.includes('blog') && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('campaigns:registration.v2.blogConditions')}</label>
          <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-50"><tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600">#</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">{t('campaigns:registration.v2.item')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">{t('campaigns:registration.v2.quantity')}</th>
              <th className="px-3 py-2"></th>
            </tr></thead>
            <tbody>
              {blogFields.map((f, i) => (
                <tr key={f.id} className="border-t border-gray-100">
                  <td className="px-3 py-2">{i + 1}</td>
                  <td className="px-3 py-2">
                    <Controller name={`publishingConditions.blog.conditions.${i}.item`} control={control}
                      render={({ field }) => (
                        <select {...field} className="px-2 py-1 text-sm border border-gray-300 rounded">
                          <option value="">{t('common:select')}</option>
                          {BLOG_ITEMS.map(it => <option key={it} value={it}>{t(`campaigns:registration.v2.blogItems.${it}`)}</option>)}
                        </select>
                      )} />
                  </td>
                  <td className="px-3 py-2">
                    <Controller name={`publishingConditions.blog.conditions.${i}.quantity`} control={control}
                      render={({ field }) => <input {...field} type="number" min={1} className="w-16 px-2 py-1 text-sm border border-gray-300 rounded" />} />
                  </td>
                  <td className="px-3 py-2"><button type="button" onClick={() => blogRemove(i)}><XMarkIcon className="w-4 h-4 text-gray-400" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" onClick={() => blogAppend({ item: '', quantity: 1 })}
            className="mt-2 flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
            <PlusIcon className="w-4 h-4" /> {t('campaigns:registration.v2.addRow')}
          </button>
          {/* 블로그 콘텐츠 내 삽입링크 */}
          <Controller name="publishingConditions.blog.insertLink" control={control}
            render={({ field }) => (
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">콘텐츠 내 삽입링크</label>
                <input {...field} type="text" placeholder="https://..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" />
              </div>
            )}
          />
        </div>
      )}

      {/* 유튜브 발행조건 */}
      {selectedChannels.includes('youtube') && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('campaigns:registration.v2.ytConditions')}</label>
          <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-50"><tr>
              <th className="px-3 py-2 text-left font-medium text-gray-600">#</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">{t('campaigns:registration.v2.item')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">{t('campaigns:registration.v2.quantity')}</th>
              <th className="px-3 py-2"></th>
            </tr></thead>
            <tbody>
              {ytFields.map((f, i) => (
                <tr key={f.id} className="border-t border-gray-100">
                  <td className="px-3 py-2">{i + 1}</td>
                  <td className="px-3 py-2">
                    <Controller name={`publishingConditions.youtube.conditions.${i}.item`} control={control}
                      render={({ field }) => (
                        <select {...field} className="px-2 py-1 text-sm border border-gray-300 rounded">
                          <option value="">{t('common:select')}</option>
                          {YOUTUBE_ITEMS.map(it => <option key={it} value={it}>{t(`campaigns:registration.v2.ytItems.${it}`)}</option>)}
                        </select>
                      )} />
                  </td>
                  <td className="px-3 py-2">
                    <Controller name={`publishingConditions.youtube.conditions.${i}.quantity`} control={control}
                      render={({ field }) => <input {...field} type="number" min={1} className="w-16 px-2 py-1 text-sm border border-gray-300 rounded" />} />
                  </td>
                  <td className="px-3 py-2"><button type="button" onClick={() => ytRemove(i)}><XMarkIcon className="w-4 h-4 text-gray-400" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" onClick={() => ytAppend({ item: '', quantity: 1 })}
            className="mt-2 flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
            <PlusIcon className="w-4 h-4" /> {t('campaigns:registration.v2.addRow')}
          </button>
          {/* 유튜브 콘텐츠 내 삽입링크 */}
          <Controller name="publishingConditions.youtube.insertLink" control={control}
            render={({ field }) => (
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">콘텐츠 내 삽입링크</label>
                <input {...field} type="text" placeholder="https://..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" />
              </div>
            )}
          />
        </div>
      )}

      {/* 캠페인 일정 */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">{t('campaigns:registration.v2.campaignSchedule')}</label>

        {/* 모집기간 */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-600 w-36">모집기간</span>
          <Controller name="recruitmentPeriod.startDate" control={control}
            render={({ field }) => <input {...field} type="date" className="px-2 py-1 text-sm border border-gray-300 rounded" />} />
          <span className="text-gray-400">~</span>
          <Controller name="recruitmentPeriod.endDate" control={control}
            render={({ field }) => <input {...field} type="date" className="px-2 py-1 text-sm border border-gray-300 rounded" />} />
        </div>

        {/* 인플루언서 발표일 */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-600 w-36">{t('campaigns:registration.v2.announcementPeriod')}</span>
          <Controller name="announcementStartDate" control={control}
            render={({ field }) => <input {...field} type="date" disabled={alwaysOpening} className="px-2 py-1 text-sm border border-gray-300 rounded disabled:bg-gray-100" />} />
          <span className="text-gray-400">~</span>
          <Controller name="announcementEndDate" control={control}
            render={({ field }) => <input {...field} type="date" disabled={alwaysOpening} className="px-2 py-1 text-sm border border-gray-300 rounded disabled:bg-gray-100" />} />
          <label className="flex items-center gap-1 ml-2">
            <Controller name="alwaysOpening" control={control}
              render={({ field }) => <input type="checkbox" checked={field.value} onChange={field.onChange} className="w-4 h-4 text-blue-600 border-gray-300 rounded" />} />
            <span className="text-sm text-gray-600">상시선정</span>
          </label>
        </div>

        {/* 방문기간 */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-600 w-36">{t('campaigns:registration.v2.visitPeriod')}</span>
          <Controller name="visitStartDate" control={control}
            render={({ field }) => <input {...field} type="date" className="px-2 py-1 text-sm border border-gray-300 rounded" />} />
          <span className="text-gray-400">~</span>
          <Controller name="visitEndDate" control={control}
            render={({ field }) => <input {...field} type="date" className="px-2 py-1 text-sm border border-gray-300 rounded" />} />
        </div>

        {/* 업로드 기간 */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-600 w-36">{t('campaigns:registration.v2.uploadPeriod')}</span>
          <span className="text-sm text-gray-500">{t('campaigns:registration.v2.afterVisit')}</span>
          <Controller name="uploadDeadlineDays" control={control}
            render={({ field }) => <input {...field} type="number" min={1} className="w-16 px-2 py-1 text-sm border border-gray-300 rounded" />} />
          <span className="text-sm text-gray-500">{t('campaigns:registration.v2.daysWithin')}</span>
          <label className="flex items-center gap-1 ml-2">
            <Controller name="discussSchedule" control={control}
              render={({ field }) => <input type="checkbox" checked={field.value} onChange={field.onChange} className="w-4 h-4 text-blue-600 border-gray-300 rounded" />} />
            <span className="text-sm text-gray-600">{t('campaigns:registration.v2.discussOnProgress')}</span>
          </label>
        </div>
      </div>

      {/* 콘텐츠 작성 가이드 */}
      <Controller name="contentWritingRequirements" control={control} rules={{ required: true }}
        render={({ field, fieldState }) => (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('campaigns:registration.v2.contentGuide')} <span className="text-red-500">*</span>
            </label>
            <textarea {...field} rows={4}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:outline-none ${fieldState.error ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'}`} />
          </div>
        )}
      />

      {/* 2차 활용 제출 항목 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('campaigns:registration.v2.secondarySubmissions')}</label>
        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gray-50"><tr>
            <th className="px-3 py-2 text-left font-medium text-gray-600">{t('campaigns:registration.v2.itemSelect')}</th>
            <th className="px-3 py-2 text-left font-medium text-gray-600">{t('campaigns:registration.v2.quantityVolume')}</th>
            <th className="px-3 py-2 text-left font-medium text-gray-600">{t('campaigns:registration.v2.remarks')}</th>
            <th className="px-3 py-2"></th>
          </tr></thead>
          <tbody>
            {secondaryFields.map((f, i) => {
              const itemType = watch(`secondarySubmissions.${i}.type`);
              return (
                <tr key={f.id} className="border-t border-gray-100">
                  <td className="px-3 py-2">
                    <Controller name={`secondarySubmissions.${i}.type`} control={control}
                      render={({ field }) => (
                        <select {...field} className="px-2 py-1 text-sm border border-gray-300 rounded">
                          <option value="">{t('common:select')}</option>
                          {SECONDARY_TYPES.map(st => <option key={st} value={st}>{t(`campaigns:registration.v2.secondaryTypes.${st}`)}</option>)}
                        </select>
                      )} />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <Controller name={`secondarySubmissions.${i}.quantity`} control={control}
                        render={({ field }) => <input {...field} type="number" min={1} className="w-16 px-2 py-1 text-sm border border-gray-300 rounded" />} />
                      <span className="text-xs text-gray-500">{itemType === 'video' ? t('campaigns:registration.v2.minutes') : t('campaigns:registration.v2.pieces')}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <Controller name={`secondarySubmissions.${i}.note`} control={control}
                      render={({ field }) => <input {...field} type="text" className="w-full px-2 py-1 text-sm border border-gray-300 rounded" />} />
                  </td>
                  <td className="px-3 py-2"><button type="button" onClick={() => secondaryRemove(i)}><XMarkIcon className="w-4 h-4 text-gray-400" /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <button type="button" onClick={() => secondaryAppend({ type: '', quantity: 1, note: '' })}
          className="mt-2 flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
          <PlusIcon className="w-4 h-4" /> {t('campaigns:registration.v2.addRow')}
        </button>
      </div>

      {/* 제출경로 */}
      <Controller name="submissionPath" control={control}
        render={({ field }) => (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('campaigns:registration.v2.submissionPath')}</label>
            <input {...field} type="text" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" />
          </div>
        )}
      />

      {/* 2차 활용 제출 요청 가이드 */}
      <Controller name="secondarySubmissionGuide" control={control}
        render={({ field }) => (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('campaigns:registration.v2.secondaryGuide')}</label>
            <textarea {...field} rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500" />
          </div>
        )}
      />
    </div>
  );
};

export default CampaignMissionV2;
